import React, { useEffect, useState } from "react";
import {
  Card,
  Input,
  Button,
  Flex,
  Checkbox,
  Select,
  Tag,
  Space,
  Alert,
  Popconfirm,
} from "antd";
import {
  useFetchAllergyQuery,
  useUpdateAllergyMutation,
  useDeleteAllergyMutation,
} from "./allergy.service";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "./style.css";

const allergyType = [
  "celery",
  "gluten",
  "crustaceans",
  "eggs",
  "fish",
  "lupin",
  "milk",
  "molluscs",
  "mustard",
  "nuts",
  "peanuts",
  "sesame seeds",
  "soya",
  "sulphur dioxide",
];

export default function AllergyEditNew() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [selectedDish, setSelectedDish] = useState(null);
  const [currentAllergies, setCurrentAllergies] = useState([]);
  const [selectedAllergies, setSelectedAllergies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: dishData = [], isFetching } = useFetchAllergyQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [updateAllergy] = useUpdateAllergyMutation();
  const [deleteAllergy] = useDeleteAllergyMutation();

  useEffect(() => {
    if (id && dishData.length > 0) {
      const dish = dishData.find((item) => item._id === id);
      if (dish) {
        setSelectedDish(dish);
        setCurrentAllergies(dish.allergens || []);
        setSelectedAllergies(dish.allergens || []);
      }
    }
  }, [id, dishData]);

  const handleDishSelect = (dishId) => {
    const dish = dishData.find((item) => item._id === dishId);
    if (dish) {
      setSelectedDish(dish);
      setCurrentAllergies(dish.allergens || []);
      setSelectedAllergies(dish.allergens || []);
    }
  };

  const handleAllergyToggle = (allergy) => {
    setSelectedAllergies((prev) =>
      prev.includes(allergy)
        ? prev.filter((item) => item !== allergy)
        : [...prev, allergy]
    );
  };

  const handleSave = async () => {
    if (!selectedDish) {
      toast.error(t("Please select a dish"));
      return;
    }

    setIsLoading(true);
    try {
      await updateAllergy({
        id: selectedDish._id,
        data: { allergens: selectedAllergies },
      }).unwrap();
      toast.success(t("Allergies updated successfully"));
      navigate("/allergy");
    } catch (error) {
      toast.error(t("Failed to update allergies"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDish) {
      toast.error(t("Please select a dish"));
      return;
    }

    setIsDeleting(true);
    try {
      await deleteAllergy(selectedDish._id).unwrap();
      toast.success(t("Dish deleted successfully"));
      navigate("/allergy");
    } catch (error) {
      toast.error(t("Failed to delete dish"));
    } finally {
      setIsDeleting(false);
    }
  };

  const renderAllergySection = (allergies) => (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {allergies.map((allergy) => (
        <Checkbox
          key={allergy}
          checked={selectedAllergies.includes(allergy)}
          onChange={() => handleAllergyToggle(allergy)}
        >
          <Tag color={selectedAllergies.includes(allergy) ? "blue" : "default"}>
            {t(allergy.toUpperCase())}
          </Tag>
        </Checkbox>
      ))}
    </Space>
  );

  return (
    <Card
      title={`${t("ALLERGY")} ${t("UPDATE")}`}
      className="allergy-create-page"
      extra={
        <Flex justify="center" gap={10}>
          <Button onClick={() => navigate("/allergy")}>{t("CANCEL")}</Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={isLoading}
            disabled={!selectedDish}
          >
            {t("UPDATE")}
          </Button>
          {selectedDish && (
            <Popconfirm
              title={t("DELETE_CONFIRMATION")}
              description={t("ARE_YOU_SURE_DELETE")}
              onConfirm={handleDelete}
              okText={t("YES")}
              cancelText={t("NO")}
            >
              <Button danger loading={isDeleting}>
                {t("DELETE")}
              </Button>
            </Popconfirm>
          )}
        </Flex>
      }
    >
      <div className="allergy-create-page-container">
        <div className="allergy-create-page-container-dish">
          <div>{t("DISH")}</div>
          <Select
            style={{ minWidth: "400px", marginLeft: "16px" }}
            showSearch
            placeholder={t("Select a dish")}
            loading={isFetching}
            value={selectedDish?._id}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={dishData.map((item) => ({
              label: item[`name_${language}`],
              value: item._id,
            }))}
            onSelect={handleDishSelect}
          />
        </div>

        {selectedDish && (
          <>
            <div className="allergy-create-page-container-current">
              <Alert
                message={t("Current Allergies")}
                description={
                  <Space wrap>
                    {currentAllergies.map((allergy) => (
                      <Tag key={allergy} color="green">
                        {t(allergy.toUpperCase())}
                      </Tag>
                    ))}
                    {currentAllergies.length === 0 && t("No allergies set")}
                  </Space>
                }
                type="info"
                showIcon
              />
            </div>

            <div className="allergy-create-page-container-allergy">
              <div className="allergy-create-page-container-allergy-label">
                {t("SELECT_ALLERGIES")}
              </div>
              <div className="allergy-create-page-container-allergy-grid">
                <div className="allergy-create-page-container-allergy-grid-column">
                  {renderAllergySection(allergyType.slice(0, 7))}
                </div>
                <div className="allergy-create-page-container-allergy-grid-column">
                  {renderAllergySection(allergyType.slice(7))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
